/// Pilotage — using visible marks rather than instruments to keep
/// a vessel safe. Covers the four classical techniques (transits,
/// leading lines, danger bearings, clearing lines) plus the
/// practical pilotage of harbour entries. Pure data; the apps/web
/// layer renders the diagrams.

// ─── Techniques ──────────────────────────────────────────────────

export type TechniqueCategory =
  | "transit"
  | "leading-line"
  | "danger-bearing"
  | "clearing-line";

export interface PilotageTechnique {
  id: string;
  name: string;
  category: TechniqueCategory;
  /// One-line headline.
  summary: string;
  /// Plain-English description.
  description: string;
  /// When this technique is the right choice.
  whenToUse: string;
  /// Step-by-step instructions for the helmsman.
  steps: readonly string[];
  /// Common failures and how to avoid them.
  pitfalls: readonly string[];
}

export const TECHNIQUES: Readonly<Record<string, PilotageTechnique>> =
  Object.freeze({
    transit_fix: {
      id: "transit_fix",
      name: "Position fix by two transits",
      category: "transit",
      summary:
        "Two pairs of charted marks aligned simultaneously gives a position fix without a compass.",
      description:
        "A transit is a line through two visible objects — when you see them in line, you know your position lies on the line. Two transits crossing at right angles give a perfect fix; even at a shallow angle they're far more accurate than a hand-bearing compass.",
      whenToUse:
        "Best fix available in pilotage waters when you can see the marks clearly. No compass error, no parallax, no leeway adjustment.",
      steps: [
        "Identify two pairs of charted features that line up at your position (church + chimney, beacon + headland, etc.).",
        "Note the exact moment both transits are 'in line' simultaneously.",
        "Plot each transit on the chart by drawing a line through the two marks. Where they cross is your fix.",
        "Mark the time and position in the log.",
      ],
      pitfalls: [
        "Misidentifying the marks — a church on the chart with two towers means you have to know WHICH tower.",
        "Transits at very narrow angles give a long, thin error 'cocked hat'. Aim for at least 30° between them.",
        "Watch the parallax in big swells — the mast at the back of the boat may not line up the same as the bow.",
      ],
    },
    transit_course: {
      id: "transit_course",
      name: "Steering by transit",
      category: "transit",
      summary:
        "Steer so two marks stay in line — you're holding a straight track regardless of tide or leeway.",
      description:
        "If two marks ahead line up and you steer to keep them lined up, you must be moving along the line that joins them. The technique cancels out tide and leeway automatically — drift to one side and the rear mark slides the other way relative to the front.",
      whenToUse:
        "Following a charted leading line into harbour; holding a track across a strongly tidal stretch where conventional course-to-steer is hard to calculate.",
      steps: [
        "Identify the two marks (front lower, rear higher when officially charted; any two if using a natural transit).",
        "Steer to bring them into line.",
        "Watch the rear mark relative to the front: drifts LEFT of the front = you're LEFT of the line, alter right; and vice versa.",
        "Make small corrections — the further the marks, the more sensitive the transit.",
      ],
      pitfalls: [
        "Over-correcting in choppy water — the boat swings either side of the line; aim for the average.",
        "Losing visibility of the rear mark in haze; have a backup compass bearing ready.",
        "The transit only works while you can see both marks — set a fall-back the moment they're obscured.",
      ],
    },
    leading_marks: {
      id: "leading_marks",
      name: "Leading marks (front + rear)",
      category: "leading-line",
      summary:
        "Officially charted pairs of marks — usually beacons with daymarks — designed to guide vessels down a safe channel.",
      description:
        "Charted leading lines are the dedicated form of transit: two beacons (or daymarks, or lit structures) placed deliberately by the harbour authority. The rear mark is higher than the front; from the channel they line up vertically. The chart shows the leading line as a heavy dashed line with the safe bearing printed next to it.",
      whenToUse:
        "Approaching unfamiliar harbours or transiting a buoyed channel with shoal water on either side. Many UK harbours (Falmouth, Portland, Cowes) have published leading lines.",
      steps: [
        "Identify the marks from the chart symbol — front mark usually shown as a triangle/square, rear higher and offset.",
        "Take note of the charted leading bearing.",
        "Approach so the marks come into vertical alignment.",
        "Steer to keep them aligned all the way to the inner end of the channel.",
        "At the inner mark or as charted, leave the leading line on the correct side — most charts spell out the next turn.",
      ],
      pitfalls: [
        "Leading lines often have a back-bearing too — make sure you're heading IN, not OUT.",
        "At night, look for the marks' light characteristics (front + rear may have different sectors / colours).",
        "Don't follow the leading line past its charted limit — it may run onto shoal water beyond the inner mark.",
      ],
    },
    danger_bearing: {
      id: "danger_bearing",
      name: "Danger bearing (NLT / NMT)",
      category: "danger-bearing",
      summary:
        "A single bearing to a mark that, if you stay on the safe side of it, keeps you clear of a charted hazard.",
      description:
        "Pick a prominent mark. Read off (or plot) the bearing from the hazard that gives the limit of safe water. While the actual bearing to the mark stays on the safe side of that limit, you're clear. NLT (No Less Than) means the bearing must be GREATER than the limit; NMT (No More Than) means it must be LESS.",
      whenToUse:
        "Closing a coast with a known hazard (rocks, shoal) when you have one prominent mark visible and your position is uncertain.",
      steps: [
        "Identify the hazard and a visible mark on the same side of the boat.",
        "On the chart, draw a line from the hazard's outer edge through your intended track to the mark. Note the bearing.",
        "Decide whether you must stay 'no less than' or 'no more than' that bearing.",
        "Underway, take frequent hand-bearing-compass shots of the mark.",
        "If the bearing creeps to the wrong side of your limit, alter course immediately.",
      ],
      pitfalls: [
        "Confusing NLT and NMT — write it down. 'NLT 030°' means abort if the bearing drops to 029°.",
        "Compass error — apply the local variation + deviation correction before comparing to the chart limit.",
        "A single danger bearing is brittle: lose sight of the mark and you've lost the limit. Pair it with a depth alarm or a backup transit.",
      ],
    },
    clearing_transit: {
      id: "clearing_transit",
      name: "Clearing transit (open / shut)",
      category: "clearing-line",
      summary:
        "A transit that, if you keep it 'open' or 'shut', guarantees you're on the safe side of a hazard.",
      description:
        "More robust than a single bearing because two marks define the line uniquely. From the safe side of the limit, the marks are 'open' (visibly separated); cross the line and they become 'shut' (in line). The pilotage instruction is 'keep this transit open' or 'don't shut this transit'.",
      whenToUse:
        "Skirting a charted hazard along a coast with two suitable marks visible on the same side as the danger.",
      steps: [
        "Identify two marks where, when in line, they pass over (or just beyond) the hazard.",
        "From the safe water, confirm the marks are 'open' — separated visually.",
        "Keep the marks open while running past the hazard.",
        "If the marks come into line ('shut'), you're on the limit; if they cross past in-line, you're over the hazard.",
      ],
      pitfalls: [
        "Need both marks visible — losing one means losing the transit. Have a back-up.",
        "Marks at very different ranges read very sensitively — small course changes shift the apparent gap quickly.",
      ],
    },
  });

export const TECHNIQUE_IDS: readonly string[] = Object.keys(TECHNIQUES);

// ─── Harbour-entry checklist ────────────────────────────────────

export interface HarbourEntryItem {
  id: string;
  /// Short heading.
  title: string;
  /// What to check or do.
  detail: string;
  /// Where to find the data (almanac / chart / local pilot book).
  source: string;
}

export const HARBOUR_ENTRY: readonly HarbourEntryItem[] = [
  {
    id: "approach-info",
    title: "Approach information",
    detail:
      "Identify the entrance, its position, leading marks if any, lit / unlit features, and any approach traffic separation.",
    source:
      "Reeds / Imray almanac entry; UKHO chart; harbour's own website / Notices to Mariners.",
  },
  {
    id: "tides",
    title: "Tides",
    detail:
      "HW + LW times for the entrance, height of tide at planned arrival, tidal stream rates and directions in the approach. Check tidal gates (locks, sills, banks that uncover).",
    source: "Almanac tide tables + tidal-stream atlas / chart tidal diamonds.",
  },
  {
    id: "depths",
    title: "Depths + drying features",
    detail:
      "Charted depth at the bar / entrance / berth; drying heights of any rocks or banks; vertical clearance of bridges or cables.",
    source: "Chart soundings + the height of tide you've computed.",
  },
  {
    id: "port-control",
    title: "Port-control signals + VHF",
    detail:
      "Look for International Port Traffic Signals (light stack on the signal station). Note the harbour's working VHF channel and call sign — many require radio contact before entry.",
    source:
      "Almanac entry. Most UK commercial ports work VHF Ch 12, 14, or 16; leisure harbours often have a dedicated channel.",
  },
  {
    id: "speed-limit",
    title: "Speed limit + wash",
    detail:
      "Most harbours limit speed to 4–6 knots inside the breakwater; some have a no-wash policy throughout. Always slow before passing moored vessels.",
    source: "Harbour byelaws (almanac); signed at the entrance.",
  },
  {
    id: "traffic",
    title: "Traffic + commercial vessels",
    detail:
      "Large ships have right of way in narrow channels (Rule 9). Identify the main shipping channel and stay clear of it where you can. Check AIS for inbound / outbound traffic.",
    source: "Local pilot book; AIS app or plotter; observed traffic.",
  },
  {
    id: "berthing-plan",
    title: "Berthing plan",
    detail:
      "Where will you tie up? Visitor pontoon, marina berth, anchorage, or pile mooring. Prepare lines + fenders on the correct side BEFORE entering — manoeuvring in the inner harbour with crew rigging lines is a recipe for trouble.",
    source: "Marina booking; harbour master; visitor-berth chart symbol.",
  },
  {
    id: "abort",
    title: "Abort plan",
    detail:
      "Where will you go if the entrance is impassable (sea state, port closed, fog)? Bolt-hole identified before you leave the previous port.",
    source: "Plot it on the chart at the passage-plan stage.",
  },
];

// ─── International Port Traffic Signals (IPTS) ──────────────────

export type SignalColour = "red" | "green" | "yellow" | "white";

export interface PortSignal {
  id: string;
  /// Main mast lights, vertical top → bottom. All IPTS signals
  /// (main + supplementary) carry three lights here; the
  /// supplementary "add" variants additionally carry an `auxLight`
  /// (see below) that sits on a side bracket level with the top
  /// main light.
  lights: readonly SignalColour[];
  /// Optional auxiliary lamp on a small side bracket to the LEFT of
  /// the main mast, mounted at the SAME HEIGHT as the top main
  /// light. Per R0111 this is always a yellow lamp, signalling that
  /// vessels navigating outside the main channel are exempt from
  /// the message. Absent on the five basic signals.
  auxLight?: SignalColour;
  /// Fixed or flashing.
  characteristic: "fixed" | "flashing";
  meaning: string;
  /// Headline phrase used in almanac / pilot books.
  shortMeaning: string;
  /// `main` = one of the five standard signals (no aux lamp).
  /// `supplementary` = one of the two "additional" variants
  /// (signal 2-add or 5-add), each pairing a yellow aux lamp with
  /// the same three-light pattern as the matching main signal.
  kind: "main" | "supplementary";
}

/// Source: IALA Recommendation (Normative) R0111 (E-111) — Port
/// Traffic Signals, Edition 1.3 (urn:mrn:iala:pub:r0111:ed1.3).
/// Five main signals + two additional ("add") signals that combine
/// a yellow light above the corresponding main signal to indicate
/// that vessels navigating outside the main channel need not comply.
export const PORT_SIGNALS: readonly PortSignal[] = [
  {
    id: "ipts-1",
    kind: "main",
    lights: ["red", "red", "red"],
    characteristic: "flashing",
    shortMeaning: "Serious emergency — all vessels stop",
    meaning:
      "Signal 1: serious emergency — all vessels to stop or divert according to instructions. Synchronised flashing red.",
  },
  {
    id: "ipts-2",
    kind: "main",
    lights: ["red", "red", "red"],
    characteristic: "fixed",
    shortMeaning: "Vessels shall not proceed",
    meaning:
      "Signal 2: vessels shall not proceed. Three red lights, fixed or slow occulting synchronised.",
  },
  {
    id: "ipts-3",
    kind: "main",
    lights: ["green", "green", "green"],
    characteristic: "fixed",
    shortMeaning: "Proceed — one-way traffic",
    meaning:
      "Signal 3: vessels may proceed — one-way traffic. Three green lights, fixed or slow occulting synchronised.",
  },
  {
    id: "ipts-4",
    kind: "main",
    lights: ["green", "green", "white"],
    characteristic: "fixed",
    shortMeaning: "Proceed — two-way traffic",
    meaning:
      "Signal 4: vessels may proceed — two-way traffic. Green / green / white top-to-bottom, fixed or slow occulting synchronised.",
  },
  {
    id: "ipts-5",
    kind: "main",
    lights: ["green", "white", "green"],
    characteristic: "fixed",
    shortMeaning: "Proceed only on specific order",
    meaning:
      "Signal 5: a vessel may proceed only when it has received specific order to do so. Green / white / green top-to-bottom, fixed or slow occulting synchronised.",
  },
  {
    id: "ipts-2-add",
    kind: "supplementary",
    lights: ["red", "red", "red"],
    auxLight: "yellow",
    characteristic: "fixed",
    shortMeaning: "Signal 2 — no proceed, except clear of main channel",
    meaning:
      "Signal 2 (additional): vessel shall not proceed, except that vessels which navigate OUTSIDE the main channel need not comply with the main message. Yellow side-bracket lamp level with the top red on the main mast, fixed or slow occulting synchronised.",
  },
  {
    id: "ipts-5-add",
    kind: "supplementary",
    lights: ["green", "white", "green"],
    auxLight: "yellow",
    characteristic: "fixed",
    shortMeaning: "Signal 5 — order required, except clear of main channel",
    meaning:
      "Signal 5 (additional): a vessel may proceed only when it has received specific order to do so; except that vessels navigating OUTSIDE the main channel need not comply with the main message. Yellow side-bracket lamp level with the top green on the main mast (green / white / green), fixed or slow occulting synchronised.",
  },
];
